import type { IntakeFieldErrors } from './intake-validation';
import { mapApiDetailsToFieldErrors } from './intake-errors';

export type IntakeLeadPayload = {
  givenName: string;
  /** `null` when the visitor has no last name. */
  familyName: string | null;
  email: string;
  phone: string;
  currentCountryCode: string;
  marketingConsent?: boolean;
  countryCodeOverridden?: boolean;
  /** GUS Mclid cookie value; staged on the thread until the first lead message. */
  mclid?: string;
};

export type IntakeLeadResult =
  | { ok: true }
  | {
      ok: false;
      status: number;
      message: string;
      /** Known fields from the API `details`; empty when none could be mapped. */
      fieldErrors: IntakeFieldErrors;
    };

export async function submitIntakeLead(params: {
  engageId: string;
  sessionId: string;
  threadId: string;
  data: IntakeLeadPayload;
}): Promise<IntakeLeadResult> {
  const { engageId, sessionId, threadId, data } = params;
  const base = import.meta.env.VITE_CHAT_API_ENDPOINT;
  const url = `${base}/api/chat/v1/institution/${engageId}/sessions/${sessionId}/threads/${threadId}/intake-lead`;

  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data }),
    });

    if (res.ok) return { ok: true };

    const body = await res.json().catch(() => null);
    const message =
      body && typeof body === 'object' && 'error' in body
        ? String((body as { error: unknown }).error)
        : `Request failed (${res.status})`;
    const fieldErrors =
      body && typeof body === 'object' && 'details' in body
        ? mapApiDetailsToFieldErrors((body as { details: unknown }).details)
        : {};
    return { ok: false, status: res.status, message, fieldErrors };
  } catch (err) {
    return {
      ok: false,
      status: 0,
      message: err instanceof Error ? err.message : 'Network error',
      fieldErrors: {},
    };
  }
}
